/**
 * Persist builder chat bubbles per session id across refreshes.
 *
 * Port of FlowOps `build-chat-storage.ts`: IndexedDB (large quota) so full
 * bubbles — including tool results — survive refresh without localStorage
 * truncation. In-memory write-through cache keeps appends synchronous;
 * durable IndexedDB writes are coalesced per session.
 */

import type { BuilderMessage } from "@/shared/builder-session";

const DB_NAME = "flow-builder";
const STORE_NAME = "buildChat";
const DB_VERSION = 1;

const CHAT_TYPES = new Set([
  "user",
  "assistant",
  "tool",
  "status",
  "plan",
  "intent",
  "ledger",
  "system",
]);

function devWarn(message: string, err?: unknown): void {
  if (process.env.NODE_ENV === "development") {
    console.warn(`[build-chat-storage] ${message}`, err);
  }
}

function isChatItem(value: unknown): value is BuilderMessage {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  return typeof item.id === "string" && typeof item.type === "string" && CHAT_TYPES.has(item.type);
}

function sanitizeItems(value: unknown): BuilderMessage[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isChatItem);
}

const cache = new Map<string, BuilderMessage[]>();

/** Synchronous snapshot of a session's chat, if it has been loaded/written. */
export function getCachedBuildChat(sessionId: string): BuilderMessage[] | undefined {
  const id = sessionId?.trim();
  if (!id) return undefined;
  return cache.get(id);
}

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB.open failed"));
  }).catch((err: unknown) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

async function idbGet(key: string): Promise<unknown> {
  const db = await openDb();
  return new Promise<unknown>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error(`idbGet failed for ${key}`));
  });
}

async function idbPut(key: string, value: BuilderMessage[]): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error(`idbPut failed for ${key}`));
  });
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error(`idbDelete failed for ${key}`));
  });
}

async function idbKeys(): Promise<string[]> {
  const db = await openDb();
  return new Promise<string[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAllKeys();
    req.onsuccess = () => {
      const keys = req.result ?? [];
      resolve(keys.map((key) => String(key)));
    };
    req.onerror = () => reject(req.error ?? new Error("idbKeys failed"));
  });
}

const dirtyKeys = new Set<string>();
let flushScheduled = false;

function flushWrites(): void {
  flushScheduled = false;
  if (dirtyKeys.size === 0) return;
  const keys = Array.from(dirtyKeys);
  dirtyKeys.clear();
  for (const key of keys) {
    void idbPut(key, cache.get(key) ?? []).catch((err: unknown) => {
      devWarn(`write failed for ${key}`, err);
      dirtyKeys.add(key);
      scheduleFlush();
    });
  }
}

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(flushWrites, { timeout: 500 });
  } else {
    setTimeout(flushWrites, 200);
  }
}

function enqueueWrite(key: string): void {
  if (!idbAvailable()) return;
  dirtyKeys.add(key);
  scheduleFlush();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushWrites);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushWrites();
    });
  }
}

/**
 * Load a session's chat. Resolves from the in-memory cache when present,
 * otherwise from IndexedDB. Populates the cache for subsequent synchronous reads.
 */
export async function readBuildChat(sessionId: string): Promise<BuilderMessage[]> {
  const id = sessionId?.trim();
  if (!id) return [];

  const cached = cache.get(id);
  if (cached) return cached;

  let items: BuilderMessage[] = [];
  if (idbAvailable()) {
    try {
      items = sanitizeItems(await idbGet(id));
    } catch (err) {
      devWarn(`read failed for ${id}`, err);
    }
  }

  cache.set(id, items);
  return items;
}

/**
 * Persist the full chat for a session. Updates the synchronous cache
 * immediately and enqueues a durable IndexedDB write. Fire-and-forget.
 */
export function writeBuildChat(sessionId: string, items: BuilderMessage[]): void {
  const id = sessionId?.trim();
  if (!id) return;
  cache.set(id, items);
  enqueueWrite(id);
}

/** Remove a session's chat from both the cache and IndexedDB. */
export function deleteBuildChat(sessionId: string): void {
  const id = sessionId?.trim();
  if (!id) return;
  cache.delete(id);
  dirtyKeys.delete(id);
  if (idbAvailable()) {
    void idbDelete(id).catch((err: unknown) => devWarn(`delete failed for ${id}`, err));
  }
}

/** Drop IDB blobs for sessions that should not be kept. */
export function pruneBuildChatKeys(keepSessionIds: Iterable<string>): void {
  const keep = new Set([...keepSessionIds].map((id) => id.trim()).filter(Boolean));
  for (const id of [...cache.keys()]) {
    if (!keep.has(id)) cache.delete(id);
  }
  if (!idbAvailable()) return;
  void idbKeys()
    .then((keys) => {
      for (const key of keys) {
        if (keep.has(key)) continue;
        void idbDelete(key).catch((err: unknown) => devWarn(`prune delete failed for ${key}`, err));
      }
    })
    .catch((err: unknown) => devWarn("prune keys failed", err));
}

/** Flush pending IDB writes immediately (tests / pagehide). */
export function flushBuildChatWrites(): void {
  flushWrites();
}

/** Test helper — clear cache + pending flushes. */
export function resetBuildChatStorageForTests(): void {
  cache.clear();
  dirtyKeys.clear();
  flushScheduled = false;
  dbPromise = null;
}
